import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import showToast from '../../utils/toastHelper';
import ConfirmModal from '../../components/ConfirmModal';

const PositionCandidates = () => {
  const { positionId } = useParams();
  const [position, setPosition] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  // const [users, setUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [candidateForm, setCandidateForm] = useState({
    fullName: '',
    email: '',
    bio: '',
    photoFile: null,
    photoPreview: null,
  });
  const [bioWordCount, setBioWordCount] = useState(0);

  const isSchoolMode = organization?.orgType === 'school';

  useEffect(() => {
    fetchOrganization();
    fetchPositionAndCandidates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId]);

  const fetchOrganization = async () => {
    try {
      const token = localStorage.getItem('token');
      const userRes = await axios.get(`${process.env.REACT_APP_API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (userRes.data.data?.organizationId) {
        const orgRes = await axios.get(`${process.env.REACT_APP_API_URL}/organizations/${userRes.data.data.organizationId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrganization(orgRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    }
  };

  const fetchPositionAndCandidates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/positions/${positionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosition(response.data.data);
      setCandidates(response.data.data.candidates || []);
    } catch (error) {
      console.error('Failed to fetch position:', error);
      showToast('Failed to load position', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCandidateForm({ 
        ...candidateForm, 
        photoFile: file,
        photoPreview: URL.createObjectURL(file)
      });
    }
  };

  const handleAddCandidate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Add all fields - backend will handle user creation/lookup
      formData.append('fullName', candidateForm.fullName);
      formData.append('email', candidateForm.email);
      formData.append('positionId', positionId);
      formData.append('bio', candidateForm.bio);
      
      if (candidateForm.photoFile) {
        formData.append('photo', candidateForm.photoFile);
      }
      
      await axios.post(
        `${process.env.REACT_APP_API_URL}/candidates/add-with-user`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      showToast('Candidate added successfully', 'success');
      setShowAddModal(false);
      setCandidateForm({ 
        fullName: '', 
        email: '', 
        bio: '', 
        photoFile: null,
        photoPreview: null
      });
      fetchPositionAndCandidates();
    } catch (error) {
      console.error('Failed to add candidate:', error);
      showToast(error.response?.data?.error || 'Failed to add candidate', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveCandidate = async (candidateId) => {
    setApprovingId(candidateId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/candidates/${candidateId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Candidate approved successfully', 'success');
      fetchPositionAndCandidates();
    } catch (error) {
      console.error('Failed to approve candidate:', error);
      showToast('Failed to approve candidate', 'error');
    } finally {
      setApprovingId(null);
    }
  };

  const handleDeleteClick = (candidate) => {
    setCandidateToDelete(candidate);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/candidates/${candidateToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Candidate deleted successfully', 'success');
      setShowDeleteModal(false);
      setCandidateToDelete(null);
      fetchPositionAndCandidates();
    } catch (error) {
      console.error('Failed to delete candidate:', error);
      showToast('Failed to delete candidate', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendInvitation = async (candidateId) => {
    setResendingId(candidateId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/candidates/${candidateId}/resend-invitation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Invitation email resent successfully', 'success');
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      showToast(error.response?.data?.message || 'Failed to resend invitation', 'error');
    } finally {
      setResendingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!position) {
    return (
      <div className="text-center py-5">
        <p>Category not found</p>
        <Link to="/admin/elections" className="btn btn-primary">
          Back to Elections
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <Link to={`/admin/elections/${position.electionId || position.election_id}`} style={{ color: '#6b7280', textDecoration: 'none' }}>
              <i className="bi bi-arrow-left"></i> Back to Election
            </Link>
          </div>
          <h3 className="mb-1" style={{ fontWeight: '600' }}>{position.name}</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            <span style={{ fontWeight: '500' }}>Category:</span> {position.description || 'No description provided'}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-sm"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '0.375rem 1rem',
            fontSize: '0.875rem',
            fontWeight: '500',
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Add Candidate
        </button>
      </div>

      {/* Candidates Table */}
      <div className="card">
        <div className="card-header bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>Candidates</h5>
        </div>
        <div className="card-body">
          {candidates.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-person-badge" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No candidates yet. Add your first candidate.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Bio</th>
                    <th>Account Status</th>
                    <th>Approval Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((candidate) => (
                    <tr key={candidate.id}>
                      <td style={{ fontWeight: '500' }}>{candidate.user?.fullName || 'N/A'}</td>
                      <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {candidate.user?.email || 'N/A'}
                      </td>
                      <td style={{ fontSize: '0.875rem', color: '#6b7280', maxWidth: '200px' }}>
                        {candidate.bio ? (
                          <>
                            {candidate.bio.substring(0, 50)}
                            {candidate.bio.length > 50 && '...'}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {candidate.user?.isEmailVerified ? (
                          <span className="badge bg-success" style={{ fontSize: '0.75rem' }}>
                            <i className="bi bi-check-circle me-1"></i>Active
                          </span>
                        ) : (
                          <span className="badge bg-warning" style={{ fontSize: '0.75rem' }}>
                            <i className="bi bi-clock me-1"></i>Setup Pending
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${candidate.isApproved ? 'bg-success' : 'bg-warning'}`} style={{ fontSize: '0.75rem' }}>
                          {candidate.isApproved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          {!candidate.user?.isEmailVerified && (
                            <button
                              onClick={() => handleResendInvitation(candidate.id)}
                              disabled={resendingId === candidate.id}
                              style={{
                                color: '#667eea',
                                fontSize: '1.125rem',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: resendingId === candidate.id ? 'wait' : 'pointer',
                                transition: 'color 0.2s',
                                display: 'inline-flex',
                                alignItems: 'center',
                                opacity: resendingId === candidate.id ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#5568d3'}
                              onMouseLeave={(e) => e.currentTarget.style.color = '#667eea'}
                              title="Resend Invitation Email"
                            >
                              {resendingId === candidate.id ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                              ) : (
                                <i className="bi bi-envelope-arrow-up"></i>
                              )}
                            </button>
                          )}
                          {!candidate.isApproved && (
                            <button
                              onClick={() => handleApproveCandidate(candidate.id)}
                              disabled={approvingId === candidate.id}
                              style={{
                                color: '#10b981',
                                fontSize: '1.125rem',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: approvingId === candidate.id ? 'wait' : 'pointer',
                                transition: 'color 0.2s',
                                display: 'inline-flex',
                                alignItems: 'center',
                                opacity: approvingId === candidate.id ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#059669'}
                              onMouseLeave={(e) => e.currentTarget.style.color = '#10b981'}
                              title="Approve Candidate"
                            >
                              {approvingId === candidate.id ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                              ) : (
                                <i className="bi bi-check-circle"></i>
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteClick(candidate)}
                            style={{
                              color: '#dc3545',
                              fontSize: '1.125rem',
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              transition: 'color 0.2s',
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#b02a37'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#dc3545'}
                            title="Delete Candidate"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Candidate Modal */}
      {showAddModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Candidate</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
              </div>
              <form onSubmit={handleAddCandidate}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={candidateForm.fullName}
                      onChange={(e) => setCandidateForm({ ...candidateForm, fullName: e.target.value })}
                      placeholder="Enter candidate's full name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Email {isSchoolMode ? <small className="text-muted">(optional — for sending invites)</small> : '*'}
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={candidateForm.email}
                      onChange={(e) => setCandidateForm({ ...candidateForm, email: e.target.value })}
                      placeholder={isSchoolMode ? 'Leave blank if candidate has no email' : 'candidate@example.com'}
                      required={!isSchoolMode}
                    />
                    <small className="text-muted">
                      {isSchoolMode
                        ? 'If provided, an invitation will be sent. Otherwise, the candidate is added directly.'
                        : 'If user exists, they\'ll be notified. If not, an invitation will be sent.'}
                    </small>
                  </div>

                  <hr className="my-3" />

                  {/* Profile Photo */}
                  <div className="mb-3">
                    <label className="form-label d-block">Profile Photo</label>
                    <div
                      className="border rounded p-3 text-center bg-light position-relative"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file) handlePhotoChange({ target: { files: [file] } });
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {candidateForm.photoPreview ? (
                        <div>
                          <img 
                            src={candidateForm.photoPreview} 
                            alt="Preview" 
                            style={{ 
                              width: '150px', 
                              height: '150px', 
                              objectFit: 'cover', 
                              borderRadius: '50%',
                              border: '3px solid #667eea'
                            }} 
                          />
                          <br />
                          <button 
                            type="button" 
                            onClick={() => setCandidateForm({ ...candidateForm, photoFile: null, photoPreview: null })} 
                            className="btn btn-outline-danger btn-sm mt-2"
                          >
                            Remove Photo
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="text-center text-muted">
                            <i className="bi bi-cloud-upload" style={{ fontSize: '3rem' }}></i>
                            <p className="mb-2 text-muted">Click anywhere or drag a file here to upload</p>
                          </div>
                          <input
                            id="photo-upload"
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0 }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bio */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label mb-0">Bio</label>
                      <span style={{ 
                        fontSize: '0.875rem', 
                        color: bioWordCount > 170 ? '#dc3545' : '#6b7280' 
                      }}>
                        {bioWordCount}/170 words
                      </span>
                    </div>
                    <textarea
                      className="form-control"
                      value={candidateForm.bio}
                      onChange={(e) => {
                        const text = e.target.value;
                        const words = text.trim().split(/\s+/).filter(word => word.length > 0);
                        const wordCount = text.trim() === '' ? 0 : words.length;
                        
                        if (wordCount <= 170) {
                          setCandidateForm({ ...candidateForm, bio: text });
                          setBioWordCount(wordCount);
                        } else {
                          // Don't update if exceeding limit
                          return;
                        }
                      }}
                      placeholder="Brief biography of the candidate (max 170 words)"
                      style={{ 
                        minHeight: '150px',
                        borderColor: bioWordCount > 170 ? '#dc3545' : undefined
                      }}
                    ></textarea>
                    {bioWordCount > 170 && (
                      <small className="text-danger">Bio exceeds maximum word limit</small>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)} disabled={isSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Adding...
                      </>
                    ) : (
                      'Add Candidate'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showDeleteModal}
        title="Delete Candidate"
        message={`Are you sure you want to delete ${candidateToDelete?.user?.fullName}? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setCandidateToDelete(null);
        }}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default PositionCandidates;
